import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, CheckCircle2, Search, Globe, Folder, Eye } from "lucide-react";

export interface WebsiteItem {
  id: number;
  category: string;
  title: string;
  description: string;
  tags: string[];
  url: string;
  detailsUrl?: string;
  status: string;
  iconUrl: string;
  highlightStat: { label: string; value: string };
  features: string[];
}

export const websiteData: WebsiteItem[] = [
  {
    id: 1,
    category: "templates",
    title: "Sitekitpro | Templatekit & Themes",
    description: "Sitekitpro is a premier digital marketplace for high-performance website themes, Elementor template kits, UI components, and turnkey website layouts.",
    tags: ["WordPress", "Elementor", "Template Kits", "Themes", "React"],
    url: "https://sitekitpro.com/home",
    status: "Live Production Platform",
    iconUrl: "/assets/logo/sitekitpro.png",
    highlightStat: { label: "Library Access", value: "Premium Themes & Kits" },
    features: ["One-Click Template Import", "Elementor Pro Compatible", "Fully Responsive Layouts", "SEO Optimized Codebase"]
  },
  {
    id: 2,
    category: "ecommerce",
    title: "eCom By Hickter – Real-Time Sync E-Commerce",
    description: "Real-time sync e-commerce platform, fully customized for any online business — storefront, app and admin always in sync.",
    tags: ["React", "Next.js", "TailwindCSS", "Real-Time Sync", "Node.js", "REST API"],
    url: "https://ecom.hickter.com",
    detailsUrl: "/ourproducts/app/ecom",
    status: "Live Storefront & Web Platform",
    iconUrl: "/assets/logo/ecom.png",
    highlightStat: { label: "Unified Engine", value: "Storefront + App + Admin" },
    features: ["Real-Time Sync", "Fully Customizable", "App + Website", "Orders & Inventory"]
  }
];

const CATEGORIES = [
  { id: 1, label: "All Websites", slug: "all" },
  { id: 2, label: "E-Commerce", slug: "ecommerce" },
  { id: 3, label: "Themes & TemplateKits", slug: "templates" }
];

export default function WebsiteProducts({ items = websiteData }: { items?: WebsiteItem[] }) {
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredCards = useMemo(() => {
    return items.filter((item) => {
      const matchesCategory = activeTab === "all" || item.category === activeTab;
      const matchesSearch =
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));

      return matchesCategory && matchesSearch;
    });
  }, [activeTab, searchQuery, items]);

  const handleReset = () => {
    setActiveTab("all");
    setSearchQuery("");
  };

  return (
    <div className="space-y-6 font-sans">
      {/* FILTER & SEARCH CONTROL BAR */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
        {/* Category Pills */}
        <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-1 md:pb-0">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveTab(cat.slug)}
              className={`px-3.5 py-1.5 text-xs font-semibold rounded-xl transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                activeTab === cat.slug
                  ? "bg-blue-600 dark:bg-blue-500 !text-white dark:!text-white shadow-md shadow-blue-500/20 font-extrabold"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-300 dark:border-slate-700 font-bold"
              }`}
            >
              <Folder size={14} className={activeTab === cat.slug ? "!text-white dark:!text-white" : ""} />
              <span className={activeTab === cat.slug ? "!text-white dark:!text-white font-extrabold" : ""}>{cat.label}</span>
            </button>
          ))}
        </div>

        {/* Search Bar */}
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
          <input
            type="text"
            placeholder="Search website products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-xs font-medium pl-9 pr-4 py-2 rounded-xl border border-slate-300 dark:border-slate-700 focus:border-blue-500 outline-none transition-colors"
          />
        </div>
      </div>

      {/* WEBSITE PRODUCTS GRID */}
      {filteredCards.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCards.map((item) => (
            <div
              key={item.id}
              className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-md hover:shadow-xl hover:border-blue-500/50 dark:hover:border-blue-500/50 transition-all flex flex-col justify-between space-y-5"
            >
              <div className="space-y-4">
                {/* Header Badge & Status */}
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 p-2 flex items-center justify-center shadow-xs">
                    <img
                      src={item.iconUrl}
                      alt={item.title}
                      className="w-full h-full object-contain rounded-lg"
                      onError={(e: any) => {
                        e.target.onerror = null;
                        e.target.src = "https://ik.imagekit.io/filennky/Products%20Hickter/crmsuite.png?updatedAt=1784371494392";
                      }}
                    />
                  </div>
                  <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 px-2.5 py-1 rounded-full border border-emerald-200 dark:border-emerald-500/20">
                    ● {item.status}
                  </span>
                </div>

                {/* Title & Description */}
                <div className="space-y-1.5">
                  <h3 className="text-lg font-extrabold text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 text-xs leading-relaxed font-medium">
                    {item.description}
                  </p>
                </div>

                {/* Highlight Stat */}
                <div className="p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/60 flex items-center justify-between">
                  <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400">
                    {item.highlightStat.label}
                  </span>
                  <span className="text-xs font-black text-blue-600 dark:text-blue-400">
                    {item.highlightStat.value}
                  </span>
                </div>

                {/* Features List */}
                <div className="space-y-1.5 pt-1">
                  <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400 dark:text-slate-500 block">
                    Key Features:
                  </span>
                  <div className="grid grid-cols-1 gap-1">
                    {item.features.map((feat, fIdx) => (
                      <div key={fIdx} className="flex items-center gap-2 text-xs font-medium text-slate-700 dark:text-slate-300">
                        <CheckCircle2 size={13} className="text-blue-600 dark:text-blue-400 shrink-0" />
                        <span className="truncate">{feat}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tech Tags */}
                <div className="flex flex-wrap gap-1.5 pt-2">
                  {item.tags.map((tag, tIdx) => (
                    <span key={tIdx} className="text-[10px] font-bold text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 px-2.5 py-0.5 rounded border border-blue-200 dark:border-blue-500/20">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex items-center gap-2">
                {item.detailsUrl && (
                  <Link
                    to={item.detailsUrl}
                    className="btn-outline flex-1 py-2 px-3 text-xs font-bold rounded-xl flex items-center justify-center gap-1.5"
                  >
                    <Eye size={14} />
                    <span>View Details</span>
                  </Link>
                )}
                <a
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  className={`${item.detailsUrl ? "flex-1" : "w-full"} btn-primary-blue py-2 px-3 text-xs font-bold rounded-xl flex items-center justify-center gap-1.5 !text-white shadow-md shadow-blue-500/20`}
                >
                  <Globe size={15} className="!text-white" />
                  <span className="!text-white">{item.id === 1 ? "Visit Sitekitpro" : "Visit Platform"}</span>
                  <ExternalLink size={13} className="!text-white" />
                </a>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3">
          <p className="text-slate-600 dark:text-slate-400 text-sm font-semibold">No website products match your filter parameters.</p>
          <button onClick={handleReset} className="btn-outline text-xs px-4 py-2 rounded-xl">
            Reset Filters
          </button>
        </div>
      )}
    </div>
  );
}
